import { CloseOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppButton, AppProgress } from '@/components/atoms';
import type { UploadContext, UploadPortal } from '@/types/upload';
import { useUploadDelete, useUploadFile } from '@/hooks/upload';

export interface UploadFieldValue {
  token: string;
  filename: string;
}

export interface PresignedUploadFieldProps {
  /** Upload context key — must match a BE-registered context. */
  context: UploadContext;
  /** Controlled value. Pass the token + filename currently attached to the form field. */
  value: UploadFieldValue | null;
  /** Called after a successful upload (with token + filename) or after removal (null). */
  onChange: (next: UploadFieldValue | null) => void;
  disabled?: boolean;
  /** Highlight the field when React Hook Form reports a validation error. */
  error?: boolean;
  /** Override auto-detected portal (admin/clinic/massager). */
  portal?: UploadPortal;
  siteType?: string;
  clinicNumber?: string;
  /** Notified whenever the internal upload state flips (true while PUT in progress). */
  onUploadingChange?: (isUploading: boolean) => void;
  /** Notified whenever the client-side validationError flips (true while a file fails validation). */
  onValidationErrorChange?: (hasError: boolean) => void;
  className?: string;
}

const splitFilename = (name: string): { base: string; ext: string } => {
  const dot = name.lastIndexOf('.');
  if (dot === -1) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
};

/**
 * A drop-in form field for direct-to-S3 file upload via presigned URL.
 *
 * Internally runs the 3-step flow (presign → PUT → confirm) using
 * `useUploadFile`, then stores the resulting `{ token, filename }`
 * in the controlled `value`. Works with React Hook Form's `Controller`.
 *
 * Layout: the「ファイルを選択」button is always rendered on the left; the
 * selected / uploading / confirmed filename (plus progress or × icon)
 * appears to its right without hiding the button.
 */
const PresignedUploadField = ({
  context,
  value,
  onChange,
  disabled,
  error,
  portal,
  siteType,
  clinicNumber,
  onUploadingChange,
  onValidationErrorChange,
  className,
}: PresignedUploadFieldProps) => {
  const [activeFilename, setActiveFilename] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const portalOverride = { portal, siteType, clinicNumber };

  const { upload, progress, isUploading, validationError, clearValidationError } = useUploadFile({
    context,
    ...portalOverride,
  });

  const onUploadingChangeRef = useRef(onUploadingChange);
  useEffect(() => {
    onUploadingChangeRef.current = onUploadingChange;
  });

  useEffect(() => {
    onUploadingChangeRef.current?.(isUploading);
  }, [isUploading]);

  const onValidationErrorChangeRef = useRef(onValidationErrorChange);
  useEffect(() => {
    onValidationErrorChangeRef.current = onValidationErrorChange;
  });

  useEffect(() => {
    onValidationErrorChangeRef.current?.(!!validationError);
  }, [validationError]);

  const { remove, isPending: isRemoving } = useUploadDelete({
    ...portalOverride,
    silentSuccess: true, // the parent form usually shows its own feedback
  });

  const handleFileSelected = useCallback(
    async (file: File | null) => {
      if (!file) return;
      setActiveFilename(file.name);
      const result = await upload(file);
      onChange({ token: result.token, filename: result.filename });
    },
    [upload, onChange]
  );

  const handleClearSelection = useCallback(() => {
    setActiveFilename(null);
    clearValidationError();
    if (inputRef.current) inputRef.current.value = '';
  }, [clearValidationError]);

  const handleRemove = useCallback(async () => {
    if (disabled || isRemoving) return;
    if (value?.token) {
      try {
        await remove(value.token);
      } catch {
        // Error already displayed — bail out without clearing so the user can retry
        return;
      }
    }
    setActiveFilename(null);
    if (inputRef.current) inputRef.current.value = '';
    onChange(null);
  }, [disabled, isRemoving, value?.token, remove, onChange]);

  const wrapperClass = [
    'flex items-center gap-3 w-full',
    error || validationError ? 'border border-red-500 rounded-md px-2 py-1' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasConfirmed = !!value?.token && !isUploading;
  const displayFilename = isUploading ? activeFilename : (value?.filename ?? activeFilename);
  const { base, ext } = displayFilename ? splitFilename(displayFilename) : { base: '', ext: '' };
  const pct = Math.min(99, Math.round(progress * 100));
  const buttonDisabled = disabled || isUploading || hasConfirmed;

  return (
    <div>
      <div className={wrapperClass}>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          disabled={buttonDisabled}
          onChange={e => handleFileSelected(e.target.files?.[0] ?? null)}
        />
        <AppButton
          size="small"
          disabled={buttonDisabled}
          onClick={() => inputRef.current?.click()}
          className="shrink-0"
        >
          ファイルを選択
        </AppButton>

        {displayFilename && (
          <span className="flex min-w-0 text-sm text-gray-800" title={displayFilename}>
            <span className="truncate">{base}</span>
            <span className="shrink-0">{ext}</span>
          </span>
        )}

        {isUploading && (
          <div className="w-32 shrink-0">
            <AppProgress percent={pct} size="small" showInfo />
          </div>
        )}

        {hasConfirmed && (
          <CloseOutlined
            className="text-red-500 hover:text-red-700 cursor-pointer transition-colors text-xs shrink-0"
            onClick={handleRemove}
          />
        )}

        {!isUploading && !hasConfirmed && activeFilename && validationError && (
          <CloseOutlined
            className="text-red-500 hover:text-red-700 cursor-pointer transition-colors text-xs shrink-0"
            onClick={handleClearSelection}
          />
        )}
      </div>
      {validationError && <div className="text-red-500 text-sm mt-1">{validationError}</div>}
    </div>
  );
};

export default PresignedUploadField;
